import React, { useState, useEffect, useMemo } from "react";
import { Layout, Menu } from "antd";
import {
  DashboardOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  TagsOutlined,
  DollarOutlined,
  UserOutlined,
  LogoutOutlined,
  EnvironmentOutlined,
  AimOutlined,
  SettingOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
  SwapOutlined,
  ScheduleOutlined,
  CarOutlined,
  BankOutlined,
  FileDoneOutlined,
  SecurityScanOutlined,
  UserSwitchOutlined,
  KeyOutlined,
  UngroupOutlined,
  TeamOutlined,
  StopOutlined,
  WalletOutlined,
  HistoryOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, selectPermissions } from "../store/userSlice";
import { isRouteAccessible } from "../utils/permissionUtils";
import Logo from "../assets/images/mini-logo3.png";
import { logoutUser } from "../api/auth";

const { Sider } = Layout;

const allMenuItems = [
  {
    key: "/dashboard",
    icon: <DashboardOutlined />,
    label: "Tableau de bord",
    requiredPermission: ["dashboard", "menu"],
  },
  {
    key: "/warnings",
    icon: <WarningOutlined />,
    label: "Avertissement",
    requiredPermission: ["warnings", "menu"],
  },

  {
    key: "/infractions",
    icon: <WarningOutlined />,
    label: "Infractions",
    requiredPermission: ["infractions", "menu"],
  },
  {
    key: "/print-infractions-warnings",
    icon: <FileTextOutlined />,
    label: "Impression infractions / avertissements",
    requiredPermission: [["infractions", "print_violation"], ["warnings", "print_warning"]],
  },
  {
    key: "/infractions-management",
    icon: <StopOutlined />,
    label: "Gestion Infractions",
    requiredPermission: ["infractions", "menu"],
  },

  {
    key: "/payment",
    icon: <DollarOutlined />,
    label: "Paiement",
    requiredPermission: ["payment", "menu"],
  },
  {
    key: "/ticket-parking",
    icon: <FileDoneOutlined />,
    label: "Parking Ticket",
    requiredPermission: ["ticket_parking", "menu"],
  },
  {
    key: "/transactions",
    icon: <SwapOutlined />,
    label: "Transactions",
    requiredPermission: ["transactions", "menu"],
  },

  {
    key: "/map",
    icon: <EnvironmentOutlined />,
    label: "Carte",
    requiredPermission: ["map", "menu"],
  },

  {
    key: "/clients",
    icon: <UserOutlined />,
    label: "Clients",
    requiredPermission: ["clients", "menu"],
  },
  {
    key: "/vehicles",
    icon: <CarOutlined />,
    label: "Vehicule",
    requiredPermission: ["vehicles", "menu"],
  },
  {
    key: "/subscriptions",
    icon: <UngroupOutlined />,
    label: "Abonnements",
    requiredPermission: ["subscriptions", "menu"],
  },
  {
    key: "citizen",
    icon: <WalletOutlined />,
    label: "Citizen Wallet",
    requiredPermission: ["citizen_dashboard", "menu"],
    children: [
      {
        key: "/citizen/dashboard",
        icon: <DashboardOutlined />,
        label: "Dashboard",
        requiredPermission: ["citizen_dashboard", "menu"],
      },
      {
        key: "/citizen/parking-tickets",
        icon: <FileDoneOutlined />,
        label: "Parking Tickets",
        requiredPermission: ["citizen_parking_tickets", "menu"],
      },
      {
        key: "/citizen/notified-infractions",
        icon: <WarningOutlined />,
        label: "Infractions Notifiées",
        requiredPermission: ["citizen_notified_infractions", "menu"],
      },
      {
        key: "/citizen/transactions",
        icon: <SwapOutlined />,
        label: "Transactions",
        requiredPermission: ["citizen_transaction", "menu"],
      },
      {
        key: "/citizen/consumption",
        icon: <DollarOutlined />,
        label: "Consommation",
        requiredPermission: ["citizen_transaction", "menu"],
      },
      {
        key: "/citizen/subscription-requests",
        icon: <FileDoneOutlined />,
        label: "Demandes d'abonnement",
        requiredPermission: ["citizen_subscription_request", "menu"],
      },
      {
        key: "/citizen/disputes",
        icon: <WarningOutlined />,
        label: "Contestations",
        requiredPermission: ["citizen_dispute", "menu"],
      },
      {
        key: "/citizen/complaints",
        icon: <WarningOutlined />,
        label: "Reclamations",
        requiredPermission: ["citizen_complaints", "menu"],
      },
      {
        key: "/citizen/users",
        icon: <UserOutlined />,
        label: "Utilisateurs",
        requiredPermission: ["citizen_users", "menu"],
      },
      {
        key: "/citizen/vehicles",
        icon: <CarOutlined />,
        label: "Véhicules",
        requiredPermission: ["citizen_vehicles", "menu"],
      },
      {
        key: "/citizen/parkings",
        icon: <EnvironmentOutlined />,
        label: "Parkings",
        requiredPermission: ["citizen_parkings", "menu"],
      },
      {
        key: "/citizen/delete-requests",
        icon: <StopOutlined />,
        label: "Suppression de compte",
        requiredPermission: ["citizen_delete_request", "menu"],
      },

      {
        key: "/citizen/roles",
        icon: <KeyOutlined />,
        label: "Rôles & Endpoints",
        requiredPermission: ["citizen_roles", "menu"],
      },
      {
        key: "/citizen/wallets",
        icon: <WalletOutlined />,
        label: "Portefeuilles",
        requiredPermission: ["citizen_wallets", "menu"],
      },
      {
        key: "/citizen/fatourati-payments",
        icon: <BankOutlined />,
        label: "Paiements Fatourati",
        requiredPermission: ["citizen_fatourati_payments", "menu"],
      },
      {
        key: "/citizen/cmi-payments",
        icon: <CreditCardOutlined />,
        label: "Paiements CMI",
        requiredPermission: ["citizen_cmi_payments", "menu"],
      },
      {
        key: "/citizen/logs",
        icon: <FileTextOutlined />,
        label: "Logs",
        requiredPermission: ["citizen_logs", "menu"],
      },
    ],
  },
  {
    key: "statistics",
    icon: <BarChartOutlined />,
    label: "Statistics",
    requiredPermission: ["statistics", "menu"],
    children: [
      {
        key: "/statistics/users",
        icon: <UserOutlined />,
        label: "Agents",
        requiredPermission: ["statistics_users", "menu"],
      },
      {
        key: "/statistics/infractions",
        icon: <WarningOutlined />,
        label: "Infractions",
        requiredPermission: ["statistics_infractions", "menu"],
      },
      {
        key: "/statistics/payments",
        icon: <DollarOutlined />,
        label: "Paiements",
        requiredPermission: ["statistics_payment", "menu"],
      },
      {
        key: "/statistics/payments-by-zone",
        icon: <DollarOutlined />,
        label: "Revenus par Zone",
        requiredPermission: ["statistics_payments", "menu"],
      },
      {
        key: "/statistics/warnings",
        icon: <WarningOutlined />,
        label: "Avertissements",
        requiredPermission: ["statistics_warnings", "menu"],
      },
      {
        key: "/statistics/parking-tickets",
        icon: <FileDoneOutlined />,
        label: "Parking Tickets",
        requiredPermission: ["statistics_parking_tickets", "menu"],
      },
      {
        key: "/statistics/subscriptions",
        icon: <UngroupOutlined />,
        label: "Abonnements",
        requiredPermission: ["statistics_subscriptions", "menu"],
      },
      {
        key: "/statistics/vehicles",
        icon: <CarOutlined />,
        label: "Véhicules",
        requiredPermission: ["statistics_vehicles", "menu"],
      },
      {
        key: "/statistics/unpaid-subscriptions",
        icon: <DollarOutlined />,
        label: "Abonnements Impayés",
        requiredPermission: ["statistics_unpaid_subscriptions", "menu"],
      },
      {
        key: "/statistics/paid-subscriptions",
        icon: <DollarOutlined />,
        label: "Abonnements payés",
        requiredPermission: ["statistics_paid_subscriptions", "menu"],
      },
    ],
  },

  {
    key: "Permissions",
    icon: <SecurityScanOutlined />,
    label: "Permissions",
    requiredPermission: ["agent_role", "menu"],
    children: [
      {
        key: "/agent-roles",
        icon: <UserSwitchOutlined />,
        label: "Agent Roles",
        requiredPermission: ["agent_role", "view"],
      },
      {
        key: "/roles",
        icon: <KeyOutlined />,
        label: "Roles",
        requiredPermission: ["role", "menu"],
      },
      {
        key: "/permissions",
        icon: <AimOutlined />,
        label: "Permissions",
        requiredPermission: ["permission", "menu"],
      },
    ],
  },
  {
    key: "settings",
    icon: <SettingOutlined />,
    label: "Paramètres",
    requiredPermission: ["settings", "menu"],
    children: [
      {
        key: "/zone",
        icon: <AimOutlined />,
        label: "Contrats",
        requiredPermission: ["zone", "menu"],
      },
      {
        key: "/configuration-manager",
        icon: <ScheduleOutlined />,
        label: "Planification et Tarifs",
        requiredPermission: ["configuration_manager", "menu"],
      },
      {
        key: "/users",
        icon: <UserOutlined />,
        label: "Utilisateurs",
        requiredPermission: ["users", "menu"],
      },
      {
        key: "/devices",
        icon: <SecurityScanOutlined />,
        label: "Appareils",
        requiredPermission: ["devices", "menu"],
      },
      {
        key: "/vehicle-uuids",
        label: "UUIDs Véhicules",
        icon: <KeyOutlined />,
        requiredPermission: ["vehicle_uuids", "menu"],
      },
      {
        key: "/vehicle-whitelist",
        icon: <SafetyCertificateOutlined />, // Choose an appropriate icon
        label: "Liste Blanche Véhicules",
        requiredPermission: ["whitelist_vehicles", "menu"],
      },
      {
        key: "/vehicle-blacklist",
        icon: <StopOutlined />, // Choose an appropriate icon for blacklist
        label: "Liste Noire Véhicules",
        requiredPermission: ["blacklist_vehicles", "menu"],
      },
      {
        key: "/public-institutions",
        icon: <BankOutlined />,
        label: "Organismes publics",
        requiredPermission: ["public_institutions", "menu"],
      },
      {
        key: "/type-infraction",
        icon: <TagsOutlined />,
        label: "Type d'infraction",
        requiredPermission: ["type_infraction", "menu"],
      },
      {
        key: "/type-subscription",
        icon: <UngroupOutlined />,
        label: "Type d'abonnement",
        requiredPermission: ["subscription_type", "menu"],
      },
      {
        key: "/subscription-audiences",
        icon: <TeamOutlined />,
        label: "Audiences d'abonnement",
        requiredPermission: ["subscription_audience", "menu"],
      },
      {
        key: "/motifs-annulation",
        icon: <CloseCircleOutlined />,
        label: "Motifs d'annulation",
        requiredPermission: ["motifs_annulation", "menu"],
      },
      {
        key: "/type-line",
        icon: <TagsOutlined />,
        label: "Type de lignes",
        requiredPermission: ["type_line", "menu"],
      },
      {
        key: "/settings",
        icon: <SettingOutlined />,
        label: "Paramètres",
        requiredPermission: ["settings", "view"],
      },
      {
        key: "/user-critical-changes",
        icon: <HistoryOutlined />,
        label: "Modifications Critiques",
        requiredPermission: ["user_change", "menu"],
      },
      {
        key: "/data-correction",
        icon: <ToolOutlined />,
        label: "Correction de Données",
        requiredPermission: ["data_correction", "menu"],
      },
      {
        key: "/duplicated-vehicles",
        icon: <CarOutlined />,
        label: "Véhicules Dupliqués",
        requiredPermission: ["vehicles", "menu"],
      },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  const dispatch = useDispatch();
  const permissions = useSelector(selectPermissions);

  // Helper function to create menu items in the format expected by antd Menu
  const getItem = (key, icon, label, children, type) => {
    return {
      key,
      icon,
      children,
      label,
      type,
    };
  };

  // Filter menu items based on user permissions
  const menuItems = useMemo(() => {
    return allMenuItems
      .filter((item) => isRouteAccessible(permissions, item.requiredPermission))
      .map((item) => {
        if (item.children) {
          return {
            ...item,
            children: item.children.filter((child) =>
              isRouteAccessible(permissions, child.requiredPermission)
            ),
          };
        }
        return item;
      });
  }, [permissions]);

  useEffect(() => {
    const pathName = location.pathname;

    // Find the keys to select and open
    const findKeys = () => {
      let selectedKey = "";
      let openKey = "";

      // First check for exact matches in children
      for (const item of menuItems) {
        if (item.children) {
          for (const child of item.children) {
            if (
              pathName === child.key ||
              pathName.startsWith(child.key + "/")
            ) {
              selectedKey = child.key;
              openKey = item.key;
              break;
            }
          }
        }
      }

      // If no match in children, check parent items
      if (!selectedKey) {
        for (const item of menuItems) {
          if (pathName === item.key || pathName.startsWith(item.key + "/")) {
            selectedKey = item.key;
            break;
          }
        }
      }

      return { selectedKey, openKey };
    };

    const { selectedKey, openKey } = findKeys();

    if (selectedKey) {
      setSelectedKeys([selectedKey]);
    }

    if (openKey && !collapsed) {
      setOpenKeys((prev) => [...new Set([...prev, openKey])]);
    }
  }, [location.pathname, menuItems, collapsed]);

  const handleMenuClick = async (item) => {
    if (item.key === "logout") {
      await logoutUser();
      dispatch(logout());
      navigate("/login");
    } else {
      navigate(item.key);
    }
  };

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  // Transform our menu structure to the format expected by antd Menu
  const transformedMenuItems = useMemo(() => {
    const transformItems = (items) =>
      items.map((item) => {
        if (item.children && item.children.length > 0) {
          return getItem(
            item.key,
            item.icon,
            item.label,
            transformItems(item.children)
          );
        }
        return getItem(item.key, item.icon, item.label);
      });

    const items = transformItems(menuItems);
    // Add logout item
    items.push(getItem("logout", <LogoutOutlined />, "Déconnexion"));
    return items;
  }, [menuItems]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => {
        setCollapsed(value);
        if (value) {
          setOpenKeys([]);
        }
      }}
      className="min-h-screen shadow-md"
      theme="light"
      width={260}
    >
      <div className="h-16 m-4 flex items-center justify-center">
        {collapsed ? (
          <img src={Logo} alt="logo" className="h-10" />
        ) : (
          <span className="font-bold text-purple-600">PARKPASS</span>
        )}
      </div>
      <Menu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        onClick={handleMenuClick}
        className="border-r-0"
        items={transformedMenuItems}
      />
    </Sider>
  );
}
